import React from "react";
import { useAuth } from "../../hooks/useAuth";
import { TimeZone } from "../../types";

export interface IUserContext {
  providerUser: {
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
    smsOptIn?: boolean;
    userId: number;
    staffId: number;
    username: string;
    subscriptionId: number;
    url: string;
    desc: string;
    hours24: boolean;
    planId: number;
    externalSubscriptionId: string;
    calendar: string;
    timeZone: TimeZone;
    token: string;
    defaultBusinessId: number;
    isPaymentMethodSelected: boolean;
    exemptedPlanId: number;
    exemptedPlanExpiresAt: string;
    defaultPageId: number;
    isAgent?: boolean;
    isConciergeAgent?: boolean;
    isDefaultConcierge?: boolean;
    isPlatformAdmin?: boolean;
  };
  login: (token: string, result?: unknown) => void;
  logout: () => void;
  setCurrentBusiness: (businessId: number) => void;
  setFnToExecuteAfterLoginSuccess: (callback: () => void) => void; // Accepts a callback function
  fnToExecute: () => void; // New function property
  updateProviderUser: (
    newUserData: Partial<IUserContext["providerUser"]>
  ) => void; // Function to update providerUser
}

export const UserContext = React.createContext<IUserContext>({
  providerUser: {
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    smsOptIn: false,
    userId: -1,
    staffId: -1,
    username: "",
    subscriptionId: -1,
    url: "",
    desc: "",
    hours24: false,
    externalSubscriptionId: "",
    planId: -1,
    calendar: "",
    timeZone: {
      id: undefined,
      name: undefined,
      timezone: "",
      is_active: true,
    },
    token: "",
    defaultBusinessId: -1,
    isPaymentMethodSelected: false,
    exemptedPlanId: -1,
    exemptedPlanExpiresAt: "",
    defaultPageId: 1,
    isAgent: false,
    isConciergeAgent: false,
    isDefaultConcierge: false,
    isPlatformAdmin: false,
  },
  login: () => { },
  logout: () => { },
  setCurrentBusiness: () => { },
  setFnToExecuteAfterLoginSuccess: () => { },
  fnToExecute: () => { },
  updateProviderUser: (
    newUserData: Partial<IUserContext["providerUser"]>
  ) => { }, // Accepts a partial object
});

interface UserProviderProps {
  children: React.ReactNode;
  value: IUserContext;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children, value }) => {
  const { clearAuthState } = useAuth();

  const enhancedValue = {
    ...value,
    logout: async () => {
      clearAuthState();
      if (value.logout) {
        await value.logout();
      }
    }
  };

  return React.createElement(UserContext.Provider,
    { value: enhancedValue },
    children
  );
};

export const UserConsumer = UserContext.Consumer;
